<template>
  <v-combobox :model-value="modelValue"
              v-model:menu="menu"
              :error-messages="categoriesErrors"
              :hide-no-data="false"
              :items="existingCategories"
              :placeholder="$t('project.question-answer.QuestionAnswerCategoriesSelect.placeholder')"
              :persistent-placeholder="true"
              :rules="[checkMaxLength]"
              append-icon=""
              menu-icon=""
              :clearable="true"
              color="blue-darken-2"
              density="compact"
              :hide-selected="true"
              item-value="id"
              item-title="name"
              multiple
              variant="outlined"
              class="categories-select"
              :menu-props="{ contentClass: 'categories-select-menu', offset: 8 }"
              @update:search="onSearchValueChange"
              @update:model-value="onModelValueChange"
              ref="combobox"
  >
    <template #chip="{ props, item, index }">
      <v-chip
        v-bind="props"
        color="white"
        class="category-chip pl-2 pr-3"
        variant="flat"
        size="default"
      >
        <app-material-icon
          name="close"
          size="xs"
          class="pa-1 mr-1 cursor-pointer"
          @click.stop="removeCategoryAtIndex(index)"
        />
        {{ item.raw.name }}
      </v-chip>
    </template>

    <template #item="{ props, item }">
      <v-list-item
        v-bind="props"
        :title="item.raw.name"
      />
    </template>

    <template #append-inner>
      <app-material-icon name="keyboard_arrow_down" color="grey-600" class="menu-icon mr-2" :class="{ 'menu-icon__rotated': menu }"/>
    </template>

    <template v-if="searchValue && searchValue.trim().length > 0" #no-data>
      <div class="create-new-category-item v-list-item v-list-item--link px-5 py-2 d-flex align-center" @click="createTemporaryCategory">
        <span class="v-list-item__overlay"></span>
        <app-material-icon name="add" color="grey-600" class="mr-2"/>
        <div class="text-grey-800">
          {{ $t('project.question-answer.QuestionAnswerCategoriesSelect.create') }} <span>"{{ searchValue }}"</span>
        </div>
      </div>
    </template>

    <template v-if="optionToCreateCategoryIsDisplayed" #append-item>
      <div class="create-new-category-item v-list-item v-list-item--link px-5 py-2 d-flex align-center" @click="createTemporaryCategory">
        <span class="v-list-item__overlay"></span>
        <app-material-icon name="add" color="grey-600" class="mr-2"/>
        <div class="text-grey-800">
          {{ $t('project.question-answer.QuestionAnswerCategoriesSelect.create') }} <span>"{{ searchValue }}"</span>
        </div>
      </div>
    </template>
  </v-combobox>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { mapActions, mapGetters, mapState } from 'vuex'

import { formFieldErrors } from '@/common/utils/form'
import { toast } from '@/common/utils/toast'
import AppMaterialIcon from '@/design-system/icons/AppMaterialIcon.vue'
import { QuestionAnswerActionTypes } from '@/store/modules/question-answer/action_types'

const CATEGORY_MAX_LENGTH = 100

export default {
  name: 'QuestionAnswerCategoriesSelect',
  components: { AppMaterialIcon },
  setup: () => ({ v$: useVuelidate() }),
  props: {
    modelValue: {
      type: Array,
      required: true,
    },
  },
  emits: ['update:model-value'],
  validations () {
    return {
      categories: {
        required,
      },
    }
  },
  data () {
    return {
      searchValue: '',
      menu: false,
    }
  },
  computed: {
    ...mapState('questionAnswer', {
      existingCategories: state => state.categories,
    }),
    ...mapGetters('room', ['roomMnemo']),
    optionToCreateCategoryIsDisplayed () {
      return this.searchValue.trim().length > 0 &&
        !this.existingCategories.find(category => category.name.toLocaleLowerCase() === this.searchValue.toLocaleLowerCase()) &&
        this.existingCategories.some(category => category.name.toLocaleLowerCase().indexOf(this.searchValue.toLocaleLowerCase()) > -1)
    },
    // computed properties is used here to be used within vuelidate validations
    categories () {
      return this.modelValue
    },
    categoriesErrors () {
      return formFieldErrors(
        this.v$.categories,
        [
          [
            'required',
            this.$t('common.validations.fieldRequired', {
              fieldName: this.$t('project.question-answer.QuestionAnswerCategoriesSelect.categories'),
            }),
          ],
        ],
      )
    },
  },
  created () {
    this.getCategories()
  },
  methods: {
    ...mapActions('questionAnswer', [QuestionAnswerActionTypes.GET_CATEGORIES]),
    async getCategories () {
      try {
        this.GET_CATEGORIES(this.roomMnemo)
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.question-answer.QuestionAnswerCategoriesSelect.getCategoriesError'))
      }
    },
    removeCategoryAtIndex (index) {
      const currentCategories = [...this.modelValue]
      currentCategories.splice(index, 1)

      this.$emit('update:model-value', currentCategories)
    },
    checkMaxLength (values) {
      for (let i = 0; i < values.length; i++) {
        if (values[i].name.length > CATEGORY_MAX_LENGTH) {
          this.removeCategoryAtIndex(i)
          toast.error(this.$t('project.question-answer.QuestionAnswerCategoriesSelect.categoryTooLong', {
            maxLength: CATEGORY_MAX_LENGTH,
          }))
          return false
        }
      }
      return true
    },
    createTemporaryCategory () {
      if (!this.searchValue) {
        return
      }

      const tempCategory = {
        id: 0,
        name: this.searchValue,
      }

      this.$emit('update:model-value', [...this.modelValue, tempCategory])
      // Reset the combobox internal value. We can't control it directly otherwise
      // We only have an _event_ for value changes.
      this.$refs.combobox.search = ''
    },
    onModelValueChange (categories) {
      const formatTempCategoryFn = (category) => {
        if (category.hasOwnProperty('id')) {
          return category
        }
        // If someone freely types a category that exists, take its existing id
        const existingCategoryId = this.existingCategories.find((existingCategory) => existingCategory.name === category)?.id
        const categoryId = existingCategoryId ?? 0

        return {
          id: categoryId,
          name: category,
        }
      }

      this.$emit('update:model-value', categories.map(formatTempCategoryFn))
    },
    onSearchValueChange (newSearchValue) {
      this.searchValue = newSearchValue
    },
  },
}
</script>

<style scoped lang="scss">
.categories-select {
  // used to display placeholder when input do not have the focus
  // known issue when using v-combobox and chips https://github.com/vuetifyjs/vuetify/issues/11553
  :deep(input) {
    max-height: inherit !important;
    padding: 0;

    &::placeholder {
      color: rgb(var(--v-theme-grey-500));
      opacity: 1;
    }
  }

  :deep(.v-field__outline) {
    --category-border-color: var(--v-theme-grey-200);
    --category-border-width: 1px;
  }

  :deep(.v-field--focused .v-field__outline) {
    --category-border-color: var(--v-theme-border-info-bold);
    --category-border-width: 2px;
  }

  :deep(.v-field__outline__start) {
    border-left-color: rgb(var(--category-border-color));
    border-left-width: var(--category-border-width);

    border-top-color: rgb(var(--category-border-color));
    border-top-width: var(--category-border-width);;

    border-bottom-color: rgb(var(--category-border-color));
    border-bottom-width: var(--category-border-width);;

    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
    opacity: 1;
  }

  :deep(.v-field__outline__end) {
    border-right-color: rgb(var(--category-border-color));
    border-right-width: var(--category-border-width);

    border-top-color: rgb(var(--category-border-color));
    border-top-width: var(--category-border-width);

    border-bottom-color: rgb(var(--category-border-color));
    border-bottom-width: var(--category-border-width);

    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
    opacity: 1;
  }
}

:global(.v-overlay__content.categories-select-menu .v-list) {
  box-shadow: 0px 0px 12px 0px #444A5014;
  border: 1px solid rgb(var(--v-theme-grey-200));
  border-radius: 8px;
  padding: 8px;
}

:global(.v-overlay__content.categories-select-menu .v-list-item) {
  padding: 0 12px;
  border-radius: 8px !important;
}

.category-chip {
  border: 1px solid rgb(var(--v-theme-grey-200));
  height: 28px;
}

.create-new-category-item {
  min-height: 48px;
}

.menu-icon {
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &__rotated {
    transform: rotate(180deg);
  }
}
</style>
